import { Component, computed, inject, output, Renderer2, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { EnpsComponent } from '@common/components/base/enps-component';
import { GlobalTablesService } from '@common/services/global-tables-service';
import { VisualStateService } from '@common/services/visual-state-service';
import { Store } from '@ngrx/store';
import { AppTheme } from '@src/suite/app.theme';
import { PSTWEB_SUITE_NAV } from '@src/suite/common/enums/constants';
import { LanguageSettings } from '@src/suite/common/services/language-settings';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { PersonalSettingsService } from '@src/suite/common/services/personal-settings-service';
import { selectStorytellingAppNavigation } from '@src/suite/common/store/global-tables/global-tables.selectors';
import { selectI18N, selectTranslatedKey } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { NavigationItemTypes } from '@src/suite/common/store/navigation/models/navigation.models';

type StorytellingIconsMapType = Record<string, string>;

@Component({
    selector: 'st-app-selector',
    imports: [MatIcon, MatIconButton, MatTooltip, RouterLink, RouterLinkActive],
    templateUrl: './app-selector.html',
    styleUrl: './app-selector.scss',
    encapsulation: ViewEncapsulation.None,
    host: {
        class: 'st-app-selector',
    },
})
export class AppSelectorComponent extends EnpsComponent {
    store = inject(Store);
    globalTablesService = inject(GlobalTablesService);
    languageSettings = this.store.selectSignal(selectI18N);
    languageSettingsOld = inject(LanguageSettings);

    private visualStateService = inject(VisualStateService);
    nomWebServicesJson = inject(NomWebServicesJson);
    personalSettingsService = inject(PersonalSettingsService);
    private appTheme = inject(AppTheme);

    NavigationItemTypes = NavigationItemTypes;
    public readonly toggleSideBar = output<void>();

    private renderer: Renderer2;

    readonly storytellingNavigation = computed(() => {
        return this.store.selectSignal(selectStorytellingAppNavigation)();
    });

    readonly isLessThanSmall = computed(() => {
        return this.visualStateService.isLessThanSmall();
    });

    isProductVisible(navigation: StorytellingNavigation): boolean {
        return !(this.isLessThanSmall() && navigation?.Product?.toLowerCase() !== 'playbook');
    }

    get storytellingIconsMap(): StorytellingIconsMapType {
        return {
            playbook: 'ads_click',
            cast: 'cell_tower',
            discover: 'newspaper',
            live: 'live_tv',
        };
    }

    get darkLightThemeTooltip(): string {
        if (this.nomWebServicesJson.HelloResults.DarkMode) {
            return this.languageSettings().PSTWebLightThemeTitleLabel;
        }

        return this.languageSettings().PSTWebDarkThemeTitleLabel;
    }

    public openInNewTab(url: string | undefined): void {
        window.open(url, '_blank');
    }

    public getSuiteNavLabel(navigation: StorytellingNavigation) {
        const suiteNavLabel = PSTWEB_SUITE_NAV;

        return this.store
            .selectSignal(selectTranslatedKey(`${suiteNavLabel}_${navigation?.ID}`))()
            .includes(suiteNavLabel)
            ? navigation?.Name
            : this.store.selectSignal(selectTranslatedKey(`${suiteNavLabel}_${navigation?.ID}`))();
    }
}
